import { get, findIndex } from "lodash";
import {
  bankConstants,
  employerConstants,
  userConstants,
  portfolioConstants,
  accountConstants,
} from "actions/types";
import type { IraAccount, RecurringContribution, UserBank } from "types/models";
import type { AccountsState, PendingClosure } from "types/state";

// Payloads originate in services across several domains (bank, employer,
// user, portfolio); they're cast once on entry. This layer is scheduled for
// deletion (Redux→Query), so typing stays shallow — see the migration doc.
interface AccountsAction {
  type: string;
  data?: unknown;
  payload?: { accountId: string; accountType: string };
}

/* Ideally, the state of the account would change quickly after the closure is initiated,
to something like AccountClosePending. We have that state, but it's several steps into the workflow.
Session storage is a workaround to prevent the user from initiating the closure again if they reload the page.
There is still a gap between the session storage being wiped and the account entering AccountClosePending
after support approves the request, and that should be fixed in the future. It does not technically cause any
problems if the user resubmits the close though. Internal will just filter out the duplicates.
 */
const initialState: AccountsState = {
  iraAccounts: [],
  bankAccounts: [],
  closeAccount: null,
  pendingClosures: (() => {
    try {
      const stored = sessionStorage.getItem("pendingClosures");
      return stored
        ? (JSON.parse(stored) as Record<string, PendingClosure>)
        : {};
    } catch (e) {
      console.error("Error loading pending closures from sessionStorage:", e);
      return {};
    }
  })(),
};

const updateAccountList = (
  accounts: IraAccount[],
  updatedAccount: IraAccount
) => {
  //Find account with same id
  const index = accounts.findIndex(
    (account) => account.id === updatedAccount.id
  );
  //Checking if account exists
  const accountExists = index !== -1;

  //Use the map to create a new array where the account with the matching id is replaced by updatedAccount otherwise gives original account
  if (accountExists) {
    return accounts.map((account) =>
      account.id === updatedAccount.id ? updatedAccount : account
    );
  }
  //If the account does not exist use spread to create new array with updatedAccount added to the end of accounts.
  return [...accounts, updatedAccount];
};

export function accounts(
  state: AccountsState = initialState,
  action: AccountsAction
): AccountsState {
  switch (action.type) {
    case accountConstants.GET_ACCOUNTS_SUCCESS: {
      const { iraAccounts, bankAccounts } = action.data as {
        iraAccounts: IraAccount[];
        bankAccounts: UserBank[];
      };
      const iraAccountIds = iraAccounts.map((account) => account.id);
      const bankAccountIds = bankAccounts.map((account) => account.id);
      return {
        ...state,
        iraAccounts: [
          // only keep accounts (IRA and Banks) that aren't being changed by the new payload
          ...state.iraAccounts.filter(
            (account) => !iraAccountIds.includes(account.id)
          ),
          ...iraAccounts,
        ],
        bankAccounts: [
          ...state.bankAccounts.filter(
            (account) => !bankAccountIds.includes(account.id)
          ),
          ...bankAccounts,
        ],
      };
    }
    case bankConstants.LINK_BANK_WITH_ICON_SUCCESS: {
      const { userBank } = action.data as { userBank: UserBank };
      return {
        ...state,
        bankAccounts: [...state.bankAccounts, userBank],
      };
    }
    case bankConstants.VERIFY_BANK_SUCCESS: {
      const verifiedBank = action.data as UserBank;
      const accounts = state.bankAccounts.filter(
        (a) => a.id !== verifiedBank.id
      );
      return {
        ...state,
        bankAccounts: [...accounts, verifiedBank],
      };
    }
    case employerConstants.GET_EMPLOYER_ACCOUNTS_SUCCESS: {
      // employerAccountList is the authoritative, complete employer bank list,
      // so replace the slice wholesale. The previous merge retained any bank
      // absent from the fresh list (and bailed out entirely when the list was
      // empty), which stranded server-deleted banks in the store until a full
      // page reload — the root cause of a removed bank lingering in the UI.
      const bankAccounts = (get(action, "data") as UserBank[]) ?? [];
      return {
        ...state,
        bankAccounts,
      };
    }
    case bankConstants.DELETE_BANK_SUCCESS:
      return {
        ...state,
        bankAccounts: state.bankAccounts.filter(
          (account) => account.id !== action.data
        ),
      };
    case accountConstants.GET_IRA_BALANCE_SUCCESS: {
      const { iraAccounts } = action.data as { iraAccounts: IraAccount[] };
      const accountsWithNewBalance = iraAccounts.reduce<
        Record<string, IraAccount>
      >((accum, account) => {
        return {
          ...accum,
          [account.id as string]: account,
        };
      }, {});
      const newIraAccounts = state.iraAccounts.map((account) => {
        if (
          Object.keys(accountsWithNewBalance).includes(account.id as string)
        ) {
          return {
            ...account,
            balances: accountsWithNewBalance[account.id as string].balances,
          };
        }
        return account;
      });
      return {
        ...state,
        iraAccounts: newIraAccounts,
      };
    }
    case bankConstants.SET_RECURRING_SCHEDULE: {
      const scheduleData = action.data as {
        bankId: string;
        recurringContribution: RecurringContribution;
      };
      const idxOfBank = findIndex(state.bankAccounts, {
        id: scheduleData.bankId,
      });
      if (idxOfBank < 0) {
        return state;
      }
      const banks = [...state.bankAccounts];
      const bankClone = { ...banks[idxOfBank] };
      bankClone.recurringContribution = scheduleData.recurringContribution;
      banks[idxOfBank] = bankClone;
      return {
        ...state,
        bankAccounts: banks,
      };
    }
    case userConstants.SUBMIT_USER_ROLLOVER_INFO_SUCCESS:
    case userConstants.ADD_ROLLOVER_ACCOUNT_DISCLOSURE_SUCCESS:
    case userConstants.USER_ROLLOVER_INVESTMENT_PROFILE_UPDATE_SUCCESS:
    case portfolioConstants.SELECT_USER_ROLLOVER_PORTFOLIO_SUCCESS:
    case userConstants.UPDATE_ROLLOVER_ACCOUNT_TERMS_SUBMIT_SUCCESS:
    case userConstants.UPDATE_PAYCHEX_ACCOUNT_TERMS_SUBMIT_SUCCESS: {
      const key = [
        "submitUserRolloverInfo",
        "addRolloverAccountDisclosure",
        "updateRolloverInvestmentProfileAndSuitability",
        "addRolloverAccountPortfolio",
        "updateRolloverAccountTermsAndSubmit",
        "updatePaychexAccountTermsAndSubmit",
      ].find((key) => Boolean(get(action.data, key)));

      const updatedAccount = get(action.data, key as string) as
        | IraAccount
        | undefined;

      if (updatedAccount) {
        const newState = {
          ...state,
          iraAccounts: updateAccountList(state.iraAccounts, updatedAccount),
        };

        return newState;
      }
      return state;
    }

    case "SET_ACCOUNT_FOR_CLOSE":
      return {
        ...state,
        closeAccount: {
          accountId: (action.payload as { accountId: string }).accountId,
          accountType: (action.payload as { accountType: string }).accountType,
        },
      };

    case "SET_ACCOUNT_PENDING_CLOSURE": {
      const payload = action.payload as {
        accountId: string;
        accountType: string;
      };
      const newState = {
        ...state,
        pendingClosures: {
          ...state.pendingClosures,
          [payload.accountId]: {
            accountType: payload.accountType,
            timestamp: Date.now(),
          },
        },
      };
      sessionStorage.setItem(
        "pendingClosures",
        JSON.stringify(newState.pendingClosures)
      );
      return newState;
    }

    default:
      return state;
  }
}
